import { useAdminApi } from "../lib/api";
import { useI18n } from "../i18n";

interface AuditRow {
  id: string;
  adminId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  ip: string;
  createdAt: string;
}

/**
 * 稽核日誌
 * ---------------------------------------------------------------
 * 每一次管理員的登入（成功與失敗）、每一次寫入操作都在這裡。
 * 出事時這是唯一能還原「誰在什麼時候從哪裡做了什麼」的依據。
 *
 * 失敗的登入特別標出來 —— 連續的失敗登入是入侵的第一個訊號。
 */
export default function Audit() {
  const { t, locale } = useI18n();
  const { data, loading, error } = useAdminApi<AuditRow[]>("/audit?limit=100");

  if (loading) return <div className="h-96 rounded-card shimmer" />;
  if (error) return <p className="rounded-card nm-inset p-6 text-sm text-brand-300">{error}</p>;

  return (
    <div className="rounded-card nm p-5">
      <h2 className="mb-4 text-sm font-semibold text-ink">{t("audit.title")}</h2>

      {(data ?? []).length === 0 ? (
        <p className="text-sm text-ink-muted">{t("audit.empty")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-[11px] text-ink-muted">
                <th className="pb-2 font-medium">{t("audit.when")}</th>
                <th className="pb-2 font-medium">{t("audit.action")}</th>
                <th className="pb-2 font-medium">{t("audit.target")}</th>
                <th className="pb-2 font-medium">IP</th>
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((row) => {
                const failed = row.action.includes("failed") || row.action.includes("blocked");
                return (
                  <tr key={row.id} className="border-t border-line">
                    <td className="whitespace-nowrap py-2.5 text-xs text-ink-muted">
                      {new Date(row.createdAt).toLocaleString(locale)}
                    </td>
                    <td className="py-2.5">
                      <span
                        className={
                          "rounded-pill px-2.5 py-1 text-[11px] font-medium " +
                          (failed ? "bg-brand-600/26 text-brand-100" : "nm-inset-sm text-ink-soft")
                        }
                      >
                        {row.action}
                      </span>
                    </td>
                    <td className="py-2.5 text-xs text-ink-soft">
                      {row.targetType ? `${row.targetType}:${row.targetId ?? "—"}` : "—"}
                    </td>
                    <td className="py-2.5 font-mono text-xs text-ink-muted">{row.ip || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
